import { FormattedMessage } from 'react-intl';
import { Col, Modal, Row } from 'antd';
import { Formik, FormikProps } from 'formik';
import { Form } from 'formik-antd';
import Button from 'components/forms/button';
import { InputText, TextArea } from 'components/inputs-formik';
import Message from 'components/message';
import LeadStatusDropdown from 'views/leads/lead-status-dropdown';
import { addLeadAsync } from 'store/leads';
import { LeadStatus } from 'types/leads';
import { Dictionary } from 'utils/constants/dictionary';
import formatPhoneNumber from 'utils/helpers/format-phone-number';
import { getLeadStatusKey } from 'utils/helpers/get-lead-status-key';
import { useAppDispatch } from 'utils/hooks';
import { useLeadValidationSchema } from 'utils/validation/schemas';
import './NewLeadModal.scss';

type FormValues = {
	email: string | null;
	name: string | null;
	phoneNumber: string | null;
	status: LeadStatus | null;
	comment: string | null;
};

type Props = {
	onClose: () => void;
};

const initialValues: FormValues = { email: null, name: null, phoneNumber: null, status: null, comment: null };
const PHONE_FORMAT = '(+55 YY XXXX XXXX)';

const InnerForm = ({
	dirty, isValid, setFieldValue, values, onClose,
}: FormikProps<FormValues> & Props): JSX.Element => {

	const handleSelect = (status: LeadStatus) => {
		setFieldValue('status', status);
	};

	return (
		<Form>
			<Row gutter={24}>
				<Col lg={{ span: 12 }} xs={{ span: 24 }}>
					<InputText
						defaultPlaceholder="Lead name"
						name="name"
						placeholderId={Dictionary.leads.newLeadModal.leadName}
					/>
				</Col>
				<Col lg={{ span: 12 }} xs={{ span: 24 }}>
					<LeadStatusDropdown
						btnCn={values.status ? 'status-selected' : undefined}
						btnDefaultText={values.status ?? 'Select status'}
						btnTextId={values.status
							? getLeadStatusKey(values.status)
							: Dictionary.leads.newLeadModal.selectStatus
						}
						highlightedBtns={values.status ? [values.status] : undefined}
						onSelect={handleSelect}
					/>
				</Col>
			</Row>
			<Row gutter={24}>
				<Col lg={{ span: 12 }} xs={{ span: 24 }}>
					<InputText
						defaultPlaceholder="Lead phone"
						name="phoneNumber"
						placeholderId={Dictionary.leads.newLeadModal.leadPhone}
						placeholderValues={{ phoneFormat: PHONE_FORMAT }}
					/>
				</Col>
				<Col lg={{ span: 12 }} xs={{ span: 24 }}>
					<InputText
						defaultPlaceholder="Lead email"
						name="email"
						placeholderId={Dictionary.leads.newLeadModal.leadEmail}
					/>
				</Col>
			</Row>

			<Row gutter={24}>
				<Col span={24}>
					<TextArea
						defaultPlaceholder="Comment"
						name="comment"
						placeholderId={Dictionary.leads.newLeadModal.comment}
					/>
				</Col>
			</Row>

			<div className="new-lead-modal__footer">
				<Button
					className="btn-green"
					defaultText="Add"
					disabled={!dirty || !isValid}
					textId={Dictionary.common.add}
					type="submit"
				/>

				<Button defaultText="Cancel" onClick={onClose} textId={Dictionary.common.cancel} />
			</div>
		</Form>
	);
};

const NewLeadModal = ({ onClose }: Props): JSX.Element => {
	const dispatch = useAppDispatch();

	const handleSubmit = (values: FormValues) => {
		const { email, name, phoneNumber, status, comment } = values;

		if (name && phoneNumber && status) {
			dispatch(addLeadAsync({ email, name, phoneNumber: formatPhoneNumber(phoneNumber), status, comment }))
				.then(res => {
					res.payload && Message.success({ messageId: Dictionary.message.success.leadAdded });
					onClose();
				});
		}
	};

	return (
		<Modal
			centered
			className="new-lead-modal"
			closable={false}
			footer={null}
			onCancel={onClose}
			title={
				<h2>
					<FormattedMessage defaultMessage="Add a new lead" id={Dictionary.leads.newLeadModal.title} />
				</h2>
			}
			visible
			width={700}
		>
			<Formik<FormValues>
				component={(formikProps) => <InnerForm onClose={onClose} {...formikProps} />}
				initialValues={initialValues}
				onSubmit={handleSubmit}
				validationSchema={useLeadValidationSchema()}
			/>
		</Modal>
	);
};

export default NewLeadModal;

